import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  IconButton,
  Typography,
} from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';

import AiInsightsIcon from '@diligentcorp/atlas-react-icons/dist/esm/lens/AiInsights.js';
import BookIcon from '@diligentcorp/atlas-react-icons/dist/esm/lens/Book.js';
import { semantic } from '@diligentcorp/atlas-design-tokens/lens';
import { InformationBanner } from 'src/components/molecules';
import { FormState } from 'src/types';
import { AiGeneratedBadge } from './AiGeneratedBadge';
import { InvestigationStepRow } from './InvestigationStepRow';
import { InvestigationPlanModalProps, InvestigationStatus } from './types';

const AI_DISCLAIMER =
  'This plan is AI-generated and may contain inaccuracies. Review all steps, questions, and findings before acting on them.';

const GENERATION_MESSAGES = [
  'Reading case summary and timeline...',
  'Identifying named parties and roles...',
  'Cross-referencing relevant company policies...',
  'Assessing severity and likely categories...',
  'Drafting investigation steps...',
  'Preparing interview questions for named parties...',
  'Preparing reporter interview questions...',
  'Compiling policy findings...',
  'Reviewing for anonymity constraints...',
  'Finalising investigation plan...',
];
const MESSAGE_INTERVAL_MS = 4000;

export const InvestigationPlanModal: React.FC<InvestigationPlanModalProps> = ({
  open,
  caseDisplayId,
  plan,
  onClose,
  onAccept,
  onSaveEdit,
}) => {
  const [showFindings, setShowFindings] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [editedSteps, setEditedSteps] = useState<
    Array<{ title: string; description: string }>
  >([]);
  const [visibleMessages, setVisibleMessages] = useState<string[]>([]);
  const messageIndexRef = useRef(0);

  const isGenerating = plan?.status === InvestigationStatus.GENERATING;
  const isFailed = plan?.status === InvestigationStatus.FAILED;
  const isAccepted = plan?.status === InvestigationStatus.ACCEPTED;

  useEffect(() => {
    if (!isGenerating) {
      setVisibleMessages([]);
      messageIndexRef.current = 0;
      return;
    }
    setVisibleMessages([GENERATION_MESSAGES[0]!]);
    messageIndexRef.current = 1;
    const interval = setInterval(() => {
      const next = messageIndexRef.current;
      if (next < GENERATION_MESSAGES.length) {
        setVisibleMessages((prev) => [...prev, GENERATION_MESSAGES[next]!]);
        messageIndexRef.current = next + 1;
      }
    }, MESSAGE_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [isGenerating]);

  const handleClose = () => {
    setShowFindings(false);
    setIsEditing(false);
    onClose();
  };

  const handleStartEdit = () => {
    if (!plan) return;
    setEditedSteps(
      plan.steps.map((s) => ({ title: s.title, description: s.description })),
    );
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditedSteps([]);
  };

  const handleSaveEdit = async () => {
    if (!plan) return;
    setIsSaving(true);
    const updatedPlan = {
      ...plan,
      steps: plan.steps.map((s, i) => ({
        ...s,
        title: editedSteps[i]?.title ?? s.title,
        description: editedSteps[i]?.description ?? s.description,
      })),
    };
    try {
      await onSaveEdit(updatedPlan);
      setIsEditing(false);
      setEditedSteps([]);
    } finally {
      setIsSaving(false);
    }
  };

  if (!plan) return null;

  const findings = plan.policyFindings;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth={false}
      sx={{
        zIndex: 2100,
        '& .MuiDialog-container': {
          justifyContent: 'center',
          alignItems: 'center',
        },
      }}
      PaperProps={{
        sx: {
          minHeight: '80vh',
          maxHeight: '90vh',
          width: '100%',
          maxWidth: '1200px',
          borderRadius: '12px',
          m: 'auto',
        },
      }}
    >
      <Box sx={{ pt: 4, px: 4, pb: 2 }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="flex-start"
          sx={{ mb: 1 }}
        >
          <Box>
            <Box display="flex" alignItems="center" gap={1.5} sx={{ mb: 1 }}>
              <Typography
                variant="h6"
                fontWeight={600}
                sx={{ fontSize: semantic.font.title.h3Lg.fontSize.value }}
              >
                Proposed Investigation Strategy: Case {caseDisplayId}
              </Typography>
              <AiGeneratedBadge data-testid="investigation-plan-ai-badge" />
            </Box>
            <Typography variant="body2" color="text.secondary">
              AI-generated plan based on case details, company policies, and
              investigation best practices.
            </Typography>
          </Box>
          <IconButton
            onClick={handleClose}
            size="small"
            aria-label="Close investigation plan"
            data-testid="investigation-plan-modal-close"
            sx={{ mt: -0.5 }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      <DialogContent sx={{ px: 4, pt: 4, pb: 4 }}>
        {!isGenerating && (
          <Box sx={{ mb: 3 }}>
            <InformationBanner
              state={FormState.INFO}
              description={AI_DISCLAIMER}
              $fullWidth
              data-testid="investigation-plan-ai-disclaimer"
            />
          </Box>
        )}
        {isGenerating ? (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            sx={{ py: 8 }}
          >
            <Box sx={{ width: '100%', maxWidth: 480 }}>
              {visibleMessages.map((msg, i) => (
                <Typography
                  key={i}
                  variant="body2"
                  color={
                    i === visibleMessages.length - 1
                      ? 'text.primary'
                      : 'text.disabled'
                  }
                  sx={{
                    mb: 1,
                    transition: 'color 0.4s',
                    fontWeight: i === visibleMessages.length - 1 ? 500 : 400,
                  }}
                >
                  {msg}
                </Typography>
              ))}
            </Box>
          </Box>
        ) : isFailed ? (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            sx={{ py: 8 }}
            data-testid="investigation-plan-failed-state"
          >
            <Typography variant="body1" color="error" sx={{ mb: 2 }}>
              Plan generation failed
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mb: 3, textAlign: 'center' }}
            >
              Something went wrong while generating the investigation plan.
            </Typography>
            <Button
              variant="outlined"
              onClick={handleClose}
              data-testid="investigation-plan-retry-button"
            >
              Close
            </Button>
          </Box>
        ) : showFindings && findings ? (
          <>
            <Box
              component="button"
              onClick={() => setShowFindings(false)}
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                mb: 5,
                color: 'text.secondary',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                p: 0,
                fontSize: semantic.font.text.md.fontSize.value,
                '&:hover': { textDecoration: 'underline' },
              }}
            >
              <ArrowBackIcon
                sx={{ mr: 0.75, fontSize: semantic.iconSize.sm.value }}
              />
              Back to Strategy Plan
            </Box>

            <Box display="flex" gap={6}>
              {/* Left: Flagged Policy Excerpts */}
              <Box flex={1}>
                <Box display="flex" alignItems="center" sx={{ mb: 3 }}>
                  <BookIcon
                    aria-hidden
                    style={{
                      marginRight: '8px',
                      width: semantic.iconSize.sm.value,
                      height: semantic.iconSize.sm.value,
                      color: 'inherit',
                    }}
                  />
                  <Typography
                    variant="overline"
                    fontWeight={600}
                    color="text.secondary"
                    sx={{
                      fontSize: semantic.font.text.sm.fontSize.value,
                      letterSpacing: 0.5,
                    }}
                  >
                    Flagged Policy Excerpts
                  </Typography>
                </Box>
                {findings.excerpts.map((excerpt) => (
                  <Box
                    key={excerpt.id}
                    sx={{
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: '8px',
                      p: 2,
                      mb: 2,
                    }}
                  >
                    <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
                      {excerpt.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ lineHeight: 1.5, fontStyle: 'italic' }}
                    >
                      &quot;{excerpt.excerpt}&quot;
                    </Typography>
                  </Box>
                ))}
              </Box>

              {/* Right: AI Analysis & Case Implications */}
              <Box flex={1}>
                <Box display="flex" alignItems="center" sx={{ mb: 3 }}>
                  <AiInsightsIcon
                    aria-hidden
                    style={{
                      marginRight: '8px',
                      width: semantic.iconSize.sm.value,
                      height: semantic.iconSize.sm.value,
                      color: 'inherit',
                    }}
                  />
                  <Typography
                    variant="overline"
                    fontWeight={600}
                    color="text.secondary"
                    sx={{
                      fontSize: semantic.font.text.sm.fontSize.value,
                      letterSpacing: 0.5,
                    }}
                  >
                    AI Analysis & Case Implications
                  </Typography>
                </Box>
                <Box
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: '8px',
                    backgroundColor: 'action.hover',
                    p: 2,
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{ lineHeight: 1.6, color: 'text.primary' }}
                  >
                    {findings.analysis}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </>
        ) : (
          <>
            {/* Column Headers */}
            <Box display="flex" gap={4} sx={{ mb: 2 }}>
              <Box flex={1}>
                <Typography
                  variant="overline"
                  fontWeight={600}
                  color="text.secondary"
                  sx={{
                    display: 'block',
                    fontSize: semantic.font.text.sm.fontSize.value,
                    letterSpacing: 0.5,
                  }}
                >
                  Investigation & Mitigation Plan
                </Typography>
              </Box>
              <Box flex={1}>
                <Typography
                  variant="overline"
                  fontWeight={600}
                  color="text.secondary"
                  sx={{
                    display: 'block',
                    fontSize: semantic.font.text.sm.fontSize.value,
                    letterSpacing: 0.5,
                  }}
                >
                  Targeted Questions & Data Requirements
                </Typography>
              </Box>
            </Box>

            {/* Column Content */}
            <Box display="flex" gap={4}>
              {/* Left Column: Investigation Steps */}
              <Box flex={1}>
                {plan.steps.map((step, i) => (
                  <InvestigationStepRow
                    key={step.id}
                    step={step}
                    index={i}
                    isEditing={isEditing}
                    editedStep={editedSteps[i]}
                    hasFindings={!!findings}
                    onTitleChange={(value) =>
                      setEditedSteps((prev) => {
                        const next = [...prev];
                        next[i] = { ...next[i]!, title: value };
                        return next;
                      })
                    }
                    onDescriptionChange={(value) =>
                      setEditedSteps((prev) => {
                        const next = [...prev];
                        next[i] = { ...next[i]!, description: value };
                        return next;
                      })
                    }
                    onViewFindings={() => setShowFindings(true)}
                  />
                ))}
              </Box>

              {/* Right Column: Targeted Questions */}
              <Box flex={1}>
                {plan.targetedQuestions.map((group) => {
                  const stakeholderType = group.stakeholderType.replace(
                    /_/g,
                    ' ',
                  );
                  return (
                    <Box key={group.id} sx={{ mb: 3 }}>
                      <Box
                        sx={{
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: '8px',
                          p: 2,
                        }}
                      >
                        <Typography
                          variant="subtitle2"
                          color="primary"
                          sx={{
                            mb: 1.5,
                            fontSize: semantic.font.text.sm.fontSize.value,
                            letterSpacing: 0.5,
                          }}
                        >
                          <Box
                            component="span"
                            sx={{ fontWeight: 700, textTransform: 'uppercase' }}
                          >
                            {stakeholderType}
                          </Box>
                          {group.stakeholderName && (
                            <Box component="span" sx={{ fontWeight: 400 }}>
                              {` - ${group.stakeholderName}`}
                            </Box>
                          )}
                        </Typography>
                        {group.questions.map((question, idx) => (
                          <Typography
                            key={idx}
                            variant="body2"
                            sx={{
                              lineHeight: 1.5,
                              minHeight: '3rem',
                              mb: idx === group.questions.length - 1 ? 0 : 1.5,
                            }}
                          >
                            {question}
                          </Typography>
                        ))}
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          </>
        )}
      </DialogContent>

      {!isGenerating && !isFailed && !showFindings && (
        <>
          <Divider />
          <DialogActions
            sx={{ px: 3, py: 2, justifyContent: 'flex-end', gap: 1.5 }}
          >
            {isEditing ? (
              <>
                <Button
                  variant="outlined"
                  onClick={handleCancelEdit}
                  disabled={isSaving}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={handleSaveEdit}
                  disabled={isSaving}
                  sx={{ minWidth: 140 }}
                >
                  {isSaving ? 'Saving...' : 'Save Changes'}
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="outlined"
                  onClick={handleStartEdit}
                  disabled={isAccepted}
                  startIcon={<EditIcon fontSize="small" />}
                >
                  Edit Plan
                </Button>
                <Button
                  variant="contained"
                  onClick={onAccept}
                  disabled={isAccepted}
                  sx={{ minWidth: 140 }}
                >
                  Accept & Initiate
                </Button>
              </>
            )}
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};
